import * as React from "react";
import { useSearchParams } from "react-router-dom";
import styled from "@emotion/styled";
import {
  Button,
  Checkbox,
  FadeIn,
  FlexRow,
  IconChevron,
  IconX,
  Input,
  Label,
  Radio,
  Text,
} from "atoms";
import {
  badges,
  blockchains,
  doxxed,
  founderAttributes,
  artistAttributes,
  artistMediums,
  missionOrCauses,
  roadmapCategories,
} from "features/reviewable/data";
import { fieldNames } from "../utils/filterFieldNames";
import { resetFilters } from "../utils/resetFilters";

/**
 * Validation errors mapped by form field name
 */
type FilterErrors = Partial<typeof fieldNames>;

interface SearchFiltersProps {
  errors: FilterErrors | null;
  formId: string;
  formRef: React.RefObject<HTMLFormElement>;
  isLoading: boolean;
  onSubmit: React.FormEventHandler<HTMLFormElement>;
  breakpoint: string;
}

// Type radio input values
const defaultType = "all";
const types = ["artist", "project"] as const;

function SearchFilters({
  errors,
  formId,
  formRef,
  isLoading,
  onSubmit,
  breakpoint,
  ...props
}: SearchFiltersProps) {
  // Watch URL search params for change of type filter value
  const [searchParams, setSearchParams] = useSearchParams();
  const typeFilter = searchParams.get("type");
  const isArtist = typeFilter?.toLowerCase() === "artist";
  const isProject = typeFilter?.toLowerCase() === "project";
  const isTypeSelected = isArtist || isProject;

  // Set all form inputs to empty or unchecked values
  const resetFiltersHandler = () => {
    if (formRef.current) resetFilters(formRef.current, true);
  };

  // Automatically update the URL search params on change, or remove if default is selected
  const createTypeChangeHandler = (type: string) => () => {
    if (type === defaultType) {
      searchParams.delete("type");
      setSearchParams(searchParams);
    } else {
      setSearchParams({ ...searchParams, type });
    }
  };

  return (
    <form onSubmit={onSubmit} ref={formRef} id={formId} {...props}>
      <FilterList>
        {/* NOTE: radio buttons can't be de-selected, so a default option unused value is needed.
         * If type does not exist in searchParams, then this will be checked by default. */}
        <EntityControl>
          <fieldset>
            <Text bold size="xs" as="legend">
              Choose Type
            </Text>
            <FlexRow gap="10px">
              <EntityRadio
                name="type"
                label="All"
                value={defaultType}
                onChange={createTypeChangeHandler(defaultType)}
                defaultChecked
              />
              {types.map((type) => (
                <EntityRadio
                  key={type}
                  name="type"
                  label={`${type[0].toUpperCase()}${type.slice(1)}`}
                  value={type}
                  onChange={createTypeChangeHandler(type)}
                />
              ))}
            </FlexRow>
          </fieldset>
        </EntityControl>

        {isProject && (
          <>
            <FilterDropdown legend="Founder Attributes" withScroll>
              {founderAttributes.map((attr) => (
                <SCheckbox
                  name={fieldNames.founderAttributes}
                  label={attr}
                  value={attr}
                  key={attr}
                />
              ))}
            </FilterDropdown>

            <FilterDropdown legend="Mission or Cause" withScroll>
              {missionOrCauses.map((mission) => (
                <SCheckbox
                  name={fieldNames.missionOrCause}
                  label={mission}
                  value={mission}
                  key={mission}
                />
              ))}
            </FilterDropdown>

            <FilterDropdown legend="Blockchain" withScroll>
              {blockchains.map((chain) => (
                <SCheckbox
                  name={fieldNames.blockchain}
                  label={chain}
                  value={chain}
                  key={chain}
                />
              ))}
            </FilterDropdown>

            <FilterDropdown legend="Roadmap Categories">
              {roadmapCategories.map((status) => (
                <SCheckbox
                  name={fieldNames.roadmapCategories}
                  label={status}
                  value={status}
                  key={status}
                />
              ))}
            </FilterDropdown>
          </>
        )}

        {isArtist && (
          <>
            <FilterDropdown legend="Artist Attributes" withScroll>
              {artistAttributes.map((attr) => (
                <SCheckbox
                  name={fieldNames.founderAttributes}
                  label={attr}
                  value={attr}
                  key={attr}
                />
              ))}
            </FilterDropdown>

            <FilterDropdown legend="Artist Medium" withScroll>
              {artistMediums.map((medium) => (
                <SCheckbox
                  name={fieldNames.medium}
                  label={medium}
                  value={medium}
                  key={medium}
                />
              ))}
            </FilterDropdown>
          </>
        )}

        {isTypeSelected && (
          <FilterDropdown legend="Badges">
            {(isArtist ? badges.artist : badges.project).map((badge) => (
              <SCheckbox
                name={fieldNames.badges}
                label={badge}
                value={badge}
                key={badge}
              />
            ))}
          </FilterDropdown>
        )}

        {isProject && (
          <>
            <FilterDropdown legend="Doxxed">
              {doxxed.map((dox) => (
                <SCheckbox
                  name={fieldNames.doxxed}
                  label={dox}
                  value={dox}
                  key={dox}
                />
              ))}
            </FilterDropdown>

            <FilterDropdown legend="Collection Size">
              <RangeInputs breakpoint={breakpoint}>
                <SLabel>
                  Min collection size
                  <SInput
                    type="number"
                    name={fieldNames.collectionSizeGte}
                    placeholder="Min"
                    isInvalid={!!errors?.collectionSizeGte}
                    step="1"
                    min="0"
                  />
                </SLabel>

                <SLabel>
                  Max collection size
                  <SInput
                    type="number"
                    name={fieldNames.collectionSizeLte}
                    placeholder="Max"
                    isInvalid={!!errors?.collectionSizeLte}
                    step="1"
                    min="1"
                  />
                </SLabel>
              </RangeInputs>
            </FilterDropdown>
          </>
        )}

        {isArtist && (
          <FilterItem>
            <Checkbox
              label="Accepts Commissions"
              name={fieldNames.commissions}
              value="true"
            />
          </FilterItem>
        )}

        {isTypeSelected && (
          <ButtonGroup>
            <Button onClick={resetFiltersHandler} size="xs" variant="blank">
              <IconX /> Clear selected filters
            </Button>
          </ButtonGroup>
        )}
      </FilterList>

      {isTypeSelected && (
        <SubmitButton
          type="submit"
          disabled={isLoading}
          variant="primary"
          size="md"
          arrow="r"
        >
          Apply Filters
        </SubmitButton>
      )}

      {errors && (
        <ErrorList role="alert">
          {Object.entries(errors).map(([key, val]) => (
            <li key={key}>{val}</li>
          ))}
        </ErrorList>
      )}
    </form>
  );
}

interface FilterDropdownProps {
  children: React.ReactNode;
  legend: string;
  withScroll?: boolean;
}

function FilterDropdown({
  children,
  legend,
  withScroll = false,
}: FilterDropdownProps) {
  return (
    <Details withScroll={withScroll}>
      <Summary>
        {legend}
        <IconChevron size=".75em" />
      </Summary>

      <Fieldset withScroll={withScroll}>{children}</Fieldset>
    </Details>
  );
}

type WithScroll = { withScroll: boolean };

const FilterList = styled.div({
  marginBottom: "1rem",
  maxHeight: "calc(100vh - 60px - 45px - 1rem)",
  overflowY: "auto",
  paddingRight: 10,
});

const Details = styled(FadeIn)<WithScroll>(({ theme, withScroll }) => ({
  borderBottom: `1px solid ${theme.colors.fg20}`,
  position: "relative",
  "&:first-of-type": {
    borderTop: `1px solid ${theme.colors.fg20}`,
  },

  ...(withScroll
    ? {
        "&:after": {
          backgroundImage:
            "linear-gradient(to bottom, rgba(255,255,255,0) 0%, white 75%)",
          bottom: 0,
          content: "''",
          height: "2.5rem",
          left: 0,
          opacity: 0,
          pointerEvents: "none",
          position: "absolute",
          right: ".5rem",
          "@media (prefers-reduced-motion: no-preference)": {
            transition: "opacity 0.1s ease",
          },
        },
        "&[open]:after": {
          opacity: 1,
        },
      }
    : {}),
})).withComponent("details");

const Summary = styled.summary(({ theme }) => ({
  ...theme.fonts.body,
  alignItems: "center",
  borderRadius: theme.borderRadius.sm,
  cursor: "pointer",
  display: "flex",
  fontSize: 15,
  fontWeight: "bold",
  justifyContent: "space-between",
  padding: "0.8rem 0.5rem 0.8rem 0",
  backgroundColor: theme.colors.bg,
  top: 0,
  "@media (prefers-reduced-motion: no-preference)": {
    transition: "color ease 0.15s",
  },
  userSelect: "none",
  zIndex: 1, // To sit on top of Details::after scroll gradient
  "&:hover": {
    color: theme.colors.fg,
  },
  "&:focus-visible": {
    outlineOffset: -2,
    outlineColor: theme.colors.fg,
  },
  svg: {
    transform: "rotate(180deg)",
    width: ".6em",
  },
  "[open] & svg": {
    transform: "rotate(0deg)",
  },
  // Attempt to hide default arrow
  listStyleType: "none",
  "&::WebkitDetailsMarker": {
    display: "none",
  },
}));

const Fieldset = styled.fieldset<WithScroll>(({ withScroll }) => ({
  border: 0,
  margin: "0 0 .5rem",
  ...(withScroll
    ? {
        height: "fit-content",
        maxHeight: "20rem",
        padding: ".5rem .5rem 2rem", // Bottom same size as Details::after so it doesn't cover last element
        overflowY: "auto",
      }
    : {
        padding: ".5rem .5rem 1rem .5rem",
      }),
}));

const FilterItem = styled.div(({ theme }) => ({
  alignItems: "center",
  borderBottom: `1px solid ${theme.colors.fg20}`,
  fontSize: 15,
  lineHeight: "1.2",
  padding: "0.8rem 0.5rem 0.8rem 0",
  label: {
    fontWeight: "bold",
  },
}));

const SCheckbox = styled(Checkbox)({
  alignItems: "center",
  fontSize: 15,
  lineHeight: "1.2",
});

const SRadio = styled(Radio)({
  alignItems: "center",
  fontSize: 15,
  lineHeight: "1.2",
  marginTop: 0,
});

const SubmitButton = styled(Button)(({ theme }) => ({
  width: "100%",
  position: "sticky",
  justifyContent: "center",
  bottom: 0,
  zIndex: 10,
  boxShadow: "0 1.5rem 0 0 white, 0 -0.25rem 0.75rem 0.5rem white",
  marginBottom: "1rem",
  marginRight: 10,
  [theme.breakpoints.desktopLarge]: {
    bottom: "1rem",
  },
}));

const ButtonGroup = styled.div({
  display: "flex",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: 10,
  margin: "1.25rem 0",
});

type WithBreakpoint = { breakpoint: string };

const RangeInputs = styled.div<WithBreakpoint>(({ breakpoint }) => ({
  alignItems: "baseline",
  display: "flex",
  gap: "1rem",
  "& > *": {
    margin: 0,
  },
  "& > span": {
    margin: 0,
  },
  [breakpoint]: {
    alignItems: "center",
    flexDirection: "column",
  },
}));

const SLabel = styled(Label)(({ theme }) => ({
  ...theme.fonts.body,
  fontSize: 15,
  width: "100%",
}));

const SInput = styled(Input)({
  fontSize: 16,
  margin: 0,
});

const ErrorList = styled.ul(({ theme }) => ({
  marginTop: "1rem",
  marginBottom: "2rem",
  color: theme.colors.accent4,
}));

const EntityControl = styled.div({
  paddingTop: "1rem",
  paddingLeft: 1,
  margin: 0,
  fieldset: {
    margin: 0,
  },
});

const EntityRadio = styled(SRadio)(({ theme }) => ({
  border: `1px solid ${theme.colors.fg}`,
  borderRadius: theme.borderRadius.round,
  marginBottom: "1rem",
  gap: 0,
  justifyContent: "center",
  position: "relative",
  width: 90,
  padding: "3px 18px 3px 18px",
  "&:hover": {
    borderColor: theme.colors.fg,
    boxShadow: `0 0 0 1px ${theme.colors.fg}`,
  },
  "&:has(input:checked)": {
    padding: "3px 12px 3px 24px",
    borderColor: theme.colors.fg,
    boxShadow: `0 0 0 1px ${theme.colors.fg}`,
  },
  input: {
    border: 0,
    position: "absolute",
    backgroundColor: "rgb( 255 255 255 / 0 )",
    left: 3,
    opacity: 0,
    transform: "translateX(-110%)",
    "&:checked": {
      opacity: 1,
      transform: "translateX(0)",
      backgroundColor: "rgb( 255 255 255 / 0 )",
    },
  },
}));

export type { FilterErrors };
export { SearchFilters };
